import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mzc-pdc/ui";
import {
    IconAdd,
    IconAlternateEmail,
    IconMoreVert,
    IconRefresh,
    IconReply,
    IconSearch,
    IconThread,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import React, { useCallback, useEffect, useState } from "react";
import { RandomColoredAvatar } from "@components_v2/avatar/random-colored-avatar";
import { useTranslation } from "react-i18next";
import { SquareIcon } from "@components_v2/icon";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import WidgetWrap from "@components/dashboard/widgets/components/wrap";
import { getWidgetData } from "@modules/dashboard";
import { useDispatch, useSelector } from "react-redux";
import CommentDate from "@features/comments/comment-date";
import useUserDetail from "@hooks/useUserDetail";
import UserDetailPopper from "@features/user-detail-popper/user-detail-popper";

import useComments from "@hooks/useComments";
import { GroupTitleWithIcon } from "@features/comments/groups";
import PopperMenu from "@components/tooltip/PopperMenu";
import { CONTENT_TYPE } from "@constants";
const RecentComments = ({
    id,
    name,
    title,
    icon,
    color,
    widgets,
    onMove,
    onDelete,
    fullWidth,
    removable,
    logo,
    viewAllUrl,
    params,
    type,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { convertCommentToHTML } = useComments({});
    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();

    const COMMENT_TYPES = [
        {
            value: "MENTIONED_TO_ME",
            label: t(`common::label::Comments where I was mentioned`),
            icon: <IconAlternateEmail size={16} />,
        },
        {
            value: "CREATED_BY_ME",
            label: t(`common::label::Comments written by me`),
            icon: <IconReply size={16} />,
        },
        {
            value: "THREAD_I_BELONG_TO",
            label: t(`common::label::Comments in threads I belong to`),
            icon: <IconAlternateEmail size={16} />,
        },
        {
            value: "REPLIED_TO_ME",
            label: t(`common::label::Reply to my comment`),
            icon: <IconThread size={16} />,
        },
    ];

    const { widgetData } = useSelector(({ dashboard }) => ({
        widgetData: dashboard.dashboardWidgetData?.find((widget) => widget.id === id),
    }));
    const user = useSelector(({ stage }) => stage?.user);

    const [commentType, setCommentType] = useState(COMMENT_TYPES[0].value);

    const getCommentType = (value) => {
        const el = COMMENT_TYPES.find((el) => el.value === value);
        return (
            <Stack direction={"row"} gap={1} alignItems={"center"}>
                {el?.icon}
                <Typography variant={"body1_500"}>{el.label}</Typography>
            </Stack>
        );
    };
    const onHandleChangeCommentType = (e) => {
        setCommentType(e.target.value);
    };

    const getAssetMediaType = useCallback((el) => {
        const mediaType = el.thread?.parentComment
            ? el.thread?.parentComment?.thread?.context?.data?.mediaType
            : el.thread?.context?.data?.mediaType;

        return mediaType;
    });

    const getAssetName = useCallback((el) => {
        const mediaType = el.thread?.parentComment
            ? el.thread?.parentComment?.thread?.context?.data?.name
            : el.thread?.context?.data?.name;

        return mediaType;
    });

    const getWidgetDetail = useCallback(() => {
        const params = { commentSearchMode: commentType, createdById: user?.id };
        dispatch(getWidgetData({ id, params }));
    }, [commentType, user]);

    const getHtmlComments = useCallback((el) => {
        // TODO: 말줄임 표시 추가해야함 첫째 문단만 보여주기
        return `<span style="display: inline-flex;"><span class="cpm-dashboard-comment" style="flex:none; padding-right: 4px">${"Replied to : "}</span>${convertCommentToHTML(
            el,
            "cpm-dashboard-comment",
        )}</span>`;
    }, []);

    useEffect(() => {
        getWidgetDetail();
    }, [commentType]);

    return (
        <WidgetWrap id={name} onMove={onMove} fullWidth={fullWidth} blockDrag={fullWidth}>
            <Grid item>
                <Stack direction={"row"} alignItems={"center"} px={theme.spacing(2)} py={theme.spacing(1.5)}>
                    <Stack direction={"row"} gap={1} flex={1} alignItems={"center"}>
                        <SquareIcon width={20} height={16} color={color}>
                            {icon}
                        </SquareIcon>
                        <Typography variant={"body1_700"}>{t(`common::label::${title}`)}</Typography>
                        {/*<IconButton>*/}
                        {/*    <IconAdd size={16} />*/}
                        {/*</IconButton>*/}
                    </Stack>
                    <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <IconButton onClick={getWidgetDetail}>
                            <IconRefresh size={16} />
                        </IconButton>
                        <Divider orientation={"vertical"} flexItem />
                        <PopperMenu
                            ButtonComponent={
                                <IconButton component={"span"}>
                                    <IconMoreVert size={16} />
                                </IconButton>
                            }
                            menuButtons={[
                                {
                                    skipTranslationLabel: t(`common::label::Remove`, "Remove"),
                                    onClick: (e) => {
                                        onDelete(name);
                                    },
                                    enable: true,
                                    buttonDisabled: true, //!removable,
                                },
                            ]}
                        />
                    </Stack>
                </Stack>
            </Grid>
            <Grid item>
                <Select
                    fullWidth
                    sx={{
                        height: theme.spacing(6),
                        "&:hover, &.Mui-focused": {
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.greySecondary[100],
                            },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            borderRadius: "0px",
                            borderTop: `1px solid ${theme.palette.greySecondary[100]}`,
                            borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
                        },
                    }}
                    renderValue={(selected) => getCommentType(selected)}
                    value={commentType}
                    onChange={onHandleChangeCommentType}
                >
                    {COMMENT_TYPES.map((el) => (
                        <MenuItem key={el.value} value={el.value} sx={{ height: "40px" }}>
                            <Typography variant={"body1_500"}>{el.label}</Typography>
                        </MenuItem>
                    ))}
                </Select>
            </Grid>
            {widgetData && widgetData.pending ? (
                <span className={"loading"}>
                    <i className={"loading-content"} />
                </span>
            ) : (
                widgetData && (
                    <>
                        <Grid item flex={1} sx={{ overflowY: "auto" }}>
                            {widgetData.data?.results?.map((el, index) => (
                                <Stack
                                    key={el.id || index}
                                    direction={"column"}
                                    gap={theme.spacing(1.5)}
                                    px={theme.spacing(1.5)}
                                    py={theme.spacing(2)}
                                    my={theme.spacing(1)}
                                    borderBottom={
                                        index < widgetData.data?.results.length - 1
                                            ? `1px solid ${theme.palette.greySecondary[100]}`
                                            : "none"
                                    }
                                >
                                    <Stack direction={"row"} gap={theme.spacing(0.75)} alignItems={"center"}>
                                        {getAssetMediaType(el) && (
                                            <AssetTypeFlag size={"small"} type={getAssetMediaType(el)} />
                                        )}
                                        <Typography variant={"body3_500"} color={theme.palette.primary.main}>
                                            {getAssetName(el)}
                                        </Typography>
                                        {el.thread?.name && (
                                            <>
                                                <Divider
                                                    orientation={"vertical"}
                                                    flexItem
                                                    color={theme.palette.greySecondary[100]}
                                                />
                                                <Stack sx={{ minWidth: 60 }}>
                                                    <GroupTitleWithIcon
                                                        title={el.thread?.name}
                                                        isPublic={el.thread?.type === "PUBLIC"}
                                                        iconSize={16}
                                                        fontVariant={"body3_500"}
                                                    />
                                                </Stack>
                                            </>
                                        )}
                                    </Stack>

                                    <Grid container flexDirection={"row"}>
                                        <Grid item sx={{ flex: "0 0 24px", mr: theme.spacing(1.5) }}>
                                            <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
                                                <RandomColoredAvatar
                                                    size={24}
                                                    id={el.created?.by?.id}
                                                    name={el.created?.by?.name}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                            <>
                                                <Grid
                                                    item
                                                    flexDirection={"row"}
                                                    justifyContent={"space-between"}
                                                    sx={{ display: "flex", width: "100%", flex: `0 0 18px` }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            width: "100%",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <>
                                                            <Button
                                                                variant={`solid`}
                                                                color={"secondary"}
                                                                onClick={(e) =>
                                                                    handleUserProfileClick(e, el?.created?.by?.id)
                                                                }
                                                                sx={{ minWidth: 0, mr: 1.5 }}
                                                            >
                                                                <Typography variant={`body1_700`} component={"p"}>
                                                                    {el.created?.by?.name}
                                                                </Typography>
                                                            </Button>
                                                            <CommentDate date={el.created?.at} />
                                                        </>
                                                    </Box>
                                                </Grid>
                                                {/*원본 댓글*/}
                                                {el.thread?.parentComment && (
                                                    <Grid
                                                        item
                                                        sx={{
                                                            flex: 1,
                                                            mt: 1,
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: getHtmlComments(el.thread?.parentComment),
                                                        }}
                                                    ></Grid>
                                                )}
                                                {/*대댓글*/}
                                                <Grid
                                                    item
                                                    sx={{
                                                        width: "100%",
                                                        flex: 1,
                                                        mt: 1,
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: convertCommentToHTML(el) }}
                                                ></Grid>
                                            </>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            ))}
                        </Grid>
                    </>
                )
            )}
            {!userProfilePending && (
                <UserDetailPopper
                    id={openUserProfile ? "user-profile-popover" : undefined}
                    handleUserProfileClose={handleUserProfileClose}
                    userProfileAnchorEl={userProfileAnchorEl}
                    openUserProfile={openUserProfile}
                    userProfileData={userProfileData}
                />
            )}
        </WidgetWrap>
    );
};

export default RecentComments;
